import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  useTheme,
  IconButton
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TopBar from '../components/TopBar';

interface ResourcesProps {
  onLogout: () => void;
}

interface Resource {
  title: string;
  description: string;
  link: string;
}

const Resources: React.FC<ResourcesProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const publishedResources: Resource[] = [
    {
      title: "Kenya Community Health Strategy",
      description: "The Kenya Community Health Strategy is a 5 year national framework that guides the delivery of integrated, people-centered primary health care at the community level through Community Health Units, with the goal of improving health outcomes and achieving universal health coverage.",
      link: "https://drive.google.com/file/d/1Mc2LmAIWb08QdQDLOVI0VE9SCFF_lbK-/view"
    },
    {
      title: "KQMCH Standards",
      description: "The KQMCH Standards are a set of guidelines designed to ensure consistent, high-quality delivery of community health services by promoting accountability, continuous improvement, and alignment with national health goals.",
      link: "https://drive.google.com/file/d/1RUwjKd39W2a0iP-Kh-2ptYQFjbE9Usjw/view"
    },
    {
      title: "KQMCH Facilitators Manual",
      description: "The KQMCH Facilitators Manual is a comprehensive guide that equips trainers with the knowledge, tools, and step-by-step instructions needed to effectively deliver training on the Kenya Quality Model for Community Health standards and processes.",
      link: "https://drive.google.com/file/d/1QeB9LVJp-gApVNSFoJZuY4hJK8ct7nH0/view"
    }
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <TopBar onLogout={onLogout} />
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate(-1)} color="primary">
              <ArrowBackIcon />
            </IconButton>
            <FolderIcon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Published Resources
            </Typography>
          </Box>
        </Box>

        {/* Resources Grid */}
        <Grid container spacing={3}>
          {publishedResources.map((resource, index) => (
            <Grid item xs={12} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
                elevation={2}
                onClick={() => window.open(resource.link, '_blank')}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <DescriptionIcon 
                      sx={{ 
                        mr: 2, 
                        mt: 0.5,
                        color: theme.palette.primary.main,
                        fontSize: 24 
                      }} 
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: theme.palette.primary.main
                          }}
                        >
                          {resource.title}
                        </Typography>
                        <Chip 
                          label="PDF" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </Box>
                      <Typography 
                        variant="body1" 
                        color="text.secondary"
                        sx={{ lineHeight: 1.6, mb: 2 }}
                      >
                        {resource.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                  <Button
                    variant="contained"
                    startIcon={<OpenInNewIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(resource.link, '_blank');
                    }}
                    sx={{ mr: 1 }}
                  >
                    View Document
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      const link = document.createElement('a');
                      link.href = resource.link;
                      link.download = `${resource.title}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    Download
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Information */}
        <Paper 
          sx={{ 
            p: 3, 
            mt: 4, 
            backgroundColor: theme.palette.info.light,
            borderLeft: `4px solid ${theme.palette.info.main}`
          }}
          elevation={1}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> These resources are provided to support community health workers, 
            facilitators, and healthcare professionals in implementing quality improvement initiatives 
            and maintaining high standards of community health service delivery.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Resources;
